import {Skeleton} from '@chakra-ui/react';
import type {ComponentWithAs, HTMLChakraProps, PropsOf} from '@chakra-ui/system';
import {ChakraLi, ChakraDiv, ChakraSpan} from '@linx-security/ui/utils/chakra-elements';
import {dataAttr} from '@utils';
import type {ReactNode} from 'react';

import {useSlots} from '../../hooks/useSlots';

import {ListBullet} from './ListBullet';
import {useStyles} from './context';
import {parts} from './theme/anatomy';

type Props = PropsOf<typeof ChakraLi> & {
  value?: number | string | null;
  percentage?: string | ReactNode;
  label?: string | ReactNode;
  isLoaded?: boolean;
  listStyleType?: string;
  isInteractive?: boolean;
};

type SlotsConfig = {
  bullet: typeof ListBullet;
  value: typeof ListItemValue;
  label: typeof ListItemLabel;
  percentage: typeof ListItemPercentage;
};

export const ListItem: ComponentWithAs<`li`, Props> = (props: Props) => {
  const {[`item-container`]: item, [`item-content`]: itemContent} = useStyles();
  const {label, percentage, value, children, isLoaded = true, isInteractive, listStyleType, ...rest} = props;
  const [slots, childrenWithoutSlots] = useSlots<SlotsConfig, {}>(
    children,
    {
      bullet: ListBullet,
      value: ListItemValue,
      label: ListItemLabel,
      percentage: ListItemPercentage,
    },
    parts,
    {},
  );

  if (!isLoaded) {
    return <WidgetListItemSkeleton />;
  }

  const attributes = {
    [`data-interactive`]: dataAttr(isInteractive),
  };

  return (
    <ChakraLi __css={item} {...attributes} {...parts[`item-container`].attrs} {...rest}>
      {listStyleType !== `none` && (slots.bullet ?? <ListBullet type={`dot`} />)}
      <ChakraDiv __css={itemContent} {...parts[`item-content`].attrs}>
        {slots.label ?? <ListItemLabel>{label}</ListItemLabel>}
        {slots.value || (value !== undefined && value !== null && <ListItemValue>{value}</ListItemValue>)}
        {slots.percentage ?? (percentage && <ListItemPercentage>{percentage}</ListItemPercentage>)}
        {childrenWithoutSlots}
      </ChakraDiv>
    </ChakraLi>
  );
};
export type {Props as WidgetListItemProps};

export const ListItemLabel = (props: HTMLChakraProps<`span`>) => {
  const {label} = useStyles();

  return <ChakraSpan {...props} __css={label} {...parts.label.attrs} />;
};

/**
 * @description Formats the value of the list item to be displayed
 */
export const ListItemValue = (props: HTMLChakraProps<`span`>) => {
  const {value} = useStyles();

  const {children} = props;

  let formattedValue = `` as ReactNode;

  if (typeof children === `number`) {
    formattedValue = Intl.NumberFormat().format(children);
  } else if (typeof children === `string`) {
    formattedValue = children;
  } else {
    formattedValue = children;
  }

  return (
    <ChakraSpan {...props} __css={value} {...parts.value.attrs}>
      {formattedValue}
    </ChakraSpan>
  );
};

export const ListItemPercentage = (props: HTMLChakraProps<`span`>) => {
  const {percentage} = useStyles();

  let formattedValue = `N/A`;

  if (typeof props.children === `number`) {
    formattedValue = `${Intl.NumberFormat().format(props.children)}%`;
  } else if (typeof props.children === `string`) {
    formattedValue = props.children;
  }

  return (
    <ChakraSpan {...props} __css={percentage} {...parts.percentage.attrs}>
      {formattedValue}
    </ChakraSpan>
  );
};

const WidgetListItemSkeleton = () => {
  const {[`item-container`]: item, label, value: count__css} = useStyles();
  return (
    <ChakraLi __css={item} justifyContent={`space-between`}>
      <Skeleton sx={label} noOfLines={1} height={`1.5rem`} width={`160px`} maxWidth={`160px`} />
      <Skeleton
        sx={count__css}
        display={`flex`}
        noOfLines={1}
        height={`1.5rem`}
        maxWidth={`50px`}
        width={`50px`}
        justifySelf={`flex-end`}
      />
    </ChakraLi>
  );
};
